import React, { ReactNode } from 'react'
import { AdCreative } from '@marketing-milk/interfaces'
import { CreativeVerdictBadge, hasClientApproval } from './CreativeVerdictBadge'
import { CreativeMedia } from './CreativeMedia'

type Props = {
  creative: AdCreative
  selected?: boolean
  highlighted?: boolean
  /** Dims the row and blocks the click outright. */
  disabled?: boolean
  /**
   * Why this ad is withheld from being picked, named on the row. Separate from
   * `disabled` because a withheld ad that is already attached still has to be
   * clickable — see `CreativeCard`.
   */
  withheldReason?: string
  /**
   * Trailing content, supplied by whoever is using the row. The campaign picker
   * puts its ad-set letter here; screens where the letter means nothing pass
   * nothing and get an empty slot.
   */
  trailing?: ReactNode
  onSelect: (adCreative: AdCreative) => void
}

export const CreativeListRow = ({
  creative,
  selected,
  highlighted,
  disabled,
  withheldReason,
  trailing,
  onSelect,
}: Props) => {
  return (
    <div
      className={`flex items-center gap-4 w-full px-4 py-2 border-l-4 transition-colors duration-150 ${
        disabled || withheldReason ? 'opacity-50' : ''
      } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${
        selected
          ? 'bg-purple-50 border-l-purple-600'
          : highlighted
          ? 'bg-amber-50 border-l-amber-400'
          : 'border-l-transparent hover:bg-gray-50'
      }`}
      data-testid={creative.name}
      onClick={() => !disabled && onSelect(creative)}
    >
      <input
        type="checkbox"
        className="flex-shrink-0 h-4 w-4 accent-purple-600 cursor-pointer disabled:cursor-not-allowed"
        checked={Boolean(selected)}
        disabled={disabled}
        aria-label={`Select ${creative.name}`}
        // The whole row is the click target; the box is a target and a readout, not a
        // second handler — without this React reports it as an uncontrolled input.
        onChange={() => undefined}
      />

      <div className="w-14 h-14 flex-shrink-0 rounded-md overflow-hidden">
        <CreativeMedia
          creative={creative}
          className="h-14 w-14 bg-gray-200 text-[6px] leading-none text-center px-0.5"
        />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{creative.name}</p>
        <p className="text-sm text-gray-500 truncate">{creative.linkHeadline || '—'}</p>
        {withheldReason ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
            <i className="fa fa-ban" aria-hidden="true" />
            {withheldReason}
          </span>
        ) : (
          hasClientApproval(creative) && <CreativeVerdictBadge />
        )}
      </div>

      {/* Written out rather than shown as an icon with the name in a tooltip: confirming a
          batch of fifty landed on the right forms is this screen's whole job, and a tooltip
          makes that fifty hovers. Independent of the highlight, which only marks ads matching
          whichever form is selected — an ad carrying a *different* form must not read as
          unassigned. */}
      <div className="flex-shrink-0 w-64 min-w-0">
        {creative.leadCardUsed && (
          <p className="text-sm text-purple-700 font-medium truncate">
            {creative.leadCardUsed.formName}
          </p>
        )}
      </div>

      <div className="flex-shrink-0 w-24 text-right">
        {disabled ? (
          <span className="text-sm font-semibold text-gray-500">Published</span>
        ) : (
          trailing
        )}
      </div>
    </div>
  )
}
